import { useEffect, useState } from "react";
import { isSafariAudioBrowser, safariPlaybackBlob } from "./safariAudioPlayback";

/** Voice notes are small: load the complete recording to avoid Safari range stalls. */
export function useAudioBlobUrl(source?: string, attempt = 0) {
  const [state, setState] = useState<{ source?: string; url?: string; error?: boolean }>({});
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries an unchanged signed URL.
  useEffect(() => {
    if (!source) {
      setState({});
      return;
    }
    if (source.startsWith("blob:") && !isSafariAudioBrowser(navigator.userAgent)) {
      setState({ source, url: source });
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setState({ source });
    const timeout = window.setTimeout(() => {
      setState({ source, error: true });
      controller.abort();
    }, 15000);
    void fetch(source, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error("Audio download failed");
        const blob = await safariPlaybackBlob(await response.blob(), controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ source, url: objectUrl });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ source, error: true });
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source, attempt]);
  const current = state.source === source ? state : {};
  return {
    url: current.url,
    loading: Boolean(source && !current.url && !current.error),
    error: Boolean(current.error),
  };
}
